import { z } from 'zod'

import { RoleSchema } from './auth'
import { EntitlementsSchema, SitePlanSchema } from './packages'

// Control API: what the super console reads from and sends to a site (issue #76).

/** `https://host` (or `http://` for local work): an origin with no path, no trailing slash. */
const OriginSchema = z
  .string()
  .trim()
  .max(260)
  .refine((v) => {
    try {
      const u = new URL(v)
      return (u.protocol === 'https:' || u.protocol === 'http:') && u.origin === v
    } catch {
      return false
    }
  }, 'An origin such as https://example.com')

/** A lowercase hostname such as `www.example.co.id`. */
const HostnameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(253)
  .regex(/^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/, 'A hostname such as www.example.com')

/**
 * The site's public and CMS origins, set by the console when a client domain is connected (issue #83) and stored in
 * `site_documents` (`origins`). They take over from the SITE_ORIGIN / ADMIN_ORIGIN Worker vars without a redeploy.
 * `redirectHosts` are the client's extra domains (apex and www): the web Worker answers them with a 301 to the same
 * path on `siteOrigin`. Absent on values stored before a site could have more than one domain.
 */
export const ControlOriginsSchema = z.object({
  siteOrigin: OriginSchema,
  adminOrigin: OriginSchema,
  redirectHosts: z.array(HostnameSchema).max(20).optional(),
  /**
   * The client domain the mailbox takes mail for (besides SITE_ORIGIN's host, the platform address), set by the console
   * apart from the origins: switching the primary domain never moves mail. `null`: none yet. Absent on values stored
   * before it existed, which keep their old meaning: the stored site origin's domain (lib/mail/inbound.ts).
   */
  mailDomain: HostnameSchema.nullable().optional(),
})
export type ControlOrigins = z.infer<typeof ControlOriginsSchema>

export const ControlOriginsResponseSchema = z.object({
  stored: ControlOriginsSchema.nullable(),
  /** What the site uses now: the stored origins when set, else the Worker vars. */
  siteOrigin: z.string(),
  adminOrigin: z.string(),
  /** Origins the CMS accepts for sign-in and changes: the stored one and the Worker var (kept during the switch). */
  acceptedAdminOrigins: z.array(z.string()),
})
export type ControlOriginsResponse = z.infer<typeof ControlOriginsResponseSchema>

/** What the web Worker asks the API (GET /api/public/hosts): where the client's extra domains redirect to. */
export const PublicHostsSchema = z.object({ siteOrigin: z.string(), redirectHosts: z.array(z.string()) })
export type PublicHosts = z.infer<typeof PublicHostsSchema>

export const ControlUsageSchema = z.object({
  /** When the site answered. */
  time: z.string(),
  environment: z.string(),
  /** Effective origins (stored ones first, see ControlOriginsSchema). */
  siteOrigin: z.string(),
  adminOrigin: z.string(),
  /** Origins the CMS accepts; absent on sites that predate stored origins. */
  acceptedAdminOrigins: z.array(z.string()).optional(),
  plan: SitePlanSchema,
  counts: z.object({
    pages: z.number().int(),
    products: z.number().int(),
    articles: z.number().int(),
    media: z.number().int(),
    newInquiries: z.number().int(),
  }),
  /** Latest change to anything shown on the public site, for "last edited" in the console. */
  lastContentUpdate: z.string().nullable(),
  /** Sign-in goes through the central accounts service (CENTRAL_LOGIN = "1", #228). Absent on sites that predate it. */
  centralLogin: z.boolean().optional(),
})
export type ControlUsage = z.infer<typeof ControlUsageSchema>

export const ControlEntitlementsSchema = z.object({
  stored: EntitlementsSchema.nullable(),
  plan: SitePlanSchema,
})
export type ControlEntitlements = z.infer<typeof ControlEntitlementsSchema>

/** Ask a site for a one-time sign-in link for a console staff member. */
export const ControlSsoRequestSchema = z.object({
  staff: z.object({
    /** Stable id of the staff member in the console (not an email). */
    key: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
    name: z.string().trim().min(1).max(100),
  }),
  role: RoleSchema,
})
export type ControlSsoRequest = z.infer<typeof ControlSsoRequestSchema>

export const ControlSsoResponseSchema = z.object({ url: z.string(), expiresAt: z.string() })
export type ControlSsoResponse = z.infer<typeof ControlSsoResponseSchema>

export const SsoRedeemRequestSchema = z.object({ token: z.string().regex(/^[0-9a-f]{64}$/) })

/** Ask the site about the mailbox addresses on one domain (before staff move the mail domain away from it). */
export const ControlMailDomainRequestSchema = z.object({ domain: HostnameSchema })
export type ControlMailDomainRequest = z.infer<typeof ControlMailDomainRequestSchema>

/** The mailbox on one domain: its listed addresses and how much mail each has, and the unlisted (catch-all) mail. */
export const ControlMailDomainReportSchema = z.object({
  domain: z.string(),
  addresses: z.array(z.object({ address: z.string(), label: z.string(), users: z.number().int(), messages: z.number().int() })),
  /** Mail to addresses on the domain that are not in the list. */
  otherMessages: z.number().int(),
})
export type ControlMailDomainReport = z.infer<typeof ControlMailDomainReportSchema>

/** What deleting a former mail domain's mailbox removed. */
export const ControlMailPurgeResultSchema = z.object({ domain: z.string(), addresses: z.number().int(), messages: z.number().int() })
export type ControlMailPurgeResult = z.infer<typeof ControlMailPurgeResultSchema>
